import { ContentService } from '@blk/cwp-ng-framework';
import {
	AbstractRenderer,
	Alignment,
	BusinessComponent,
	BusinessComponentOptions,
	CellPanel,
	ColumnPanel,
	ImageRenderer,
	Panel,
	RowPanel,
	SquareRenderer,
	StaticParagraphComponent
} from '@blk/simba';
import { ChartType, ColumnChartName } from '@ddt-web/chart';
import { ColorsConfig } from '@ddt-web/config';
import { InjectConfig, ModelsExperienceConfigKey, SharedConfigKey } from '@ddt-web/config-layer';
import { ComponentData, DateFormatter, TabData, TabUtil } from '@ddt-web/data-service';
import { DateUtil } from '@ddt-web/generic-utils';
import { HeadingComponent, HeadingLevel, HighchartsSVGComponent, SvgImage } from '@ddt-web/pdf-components';

export class EsgUpliftComponent extends BusinessComponent {
	@InjectConfig(SharedConfigKey.EsgRatingsRangeScale) private readonly esgRatingScale = {};
	@InjectConfig(ModelsExperienceConfigKey.DATE_FORMAT) private readonly dateFormat: string = DateUtil.MODELS_US_DATE_FORMAT;

	public modelList: string[];

	constructor(name: string, public dataComponentId: string, public selectedModels?: string[], public visible: boolean = true) {
		super(name, 'BusinessComponent', BusinessComponentOptions.merge({ visible: visible, componentId: dataComponentId }));
	}

	public isComponentDataVisible(componentData: ComponentData): boolean {
		const tabData: TabData = componentData.getTabById('esgScore');
		return componentData && componentData.success && TabUtil.isTabValid(tabData);
	}

	public buildComponent(componentData: ComponentData, contentSvc: ContentService): AbstractRenderer<SVGGElement>[] {
		const panels: AbstractRenderer<SVGGElement>[] = [];

		if (this.isComponentDataVisible(componentData)) {
			panels.push(new HeadingComponent('ESG_METRICS_HEADER_PDF', 'ESG METRICS - ESG BALANCED MODEL', HeadingLevel.H1));
			panels.push(this.buildAsOfDateRow(componentData));
			panels.push(
				new StaticParagraphComponent(
					'ESG_METRICS_SUBTEXT_PDF',
					'We quantify the ESG characteristics of the model portfolio by comparing its MSCI ESG Ratings and Carbon Emissions with a non-ESG Benchmark portfolio. The Non-ESG Benchmark portfolio refers to a portfolio that uses traditional non-ESG market indices as building blocks. The following charts show the ESG Uplift and Carbon Reduction obtained from investing in the model portfolio relative to a traditional non-ESG Benchmark.',
					{},
					{ marginBottom: 15 },
					['text-10']
				)
			);

			panels.push(new HeadingComponent('ESG_UPLIFT_HEADER_PDF', 'ESG UPLIFT', HeadingLevel.H2));

			panels.push(
				new RowPanel(
					'',
					[
						new ImageRenderer(new SvgImage('TARGET').getStaticContent()),
						new StaticParagraphComponent(
							'ESG_UPLIFT_SUBTEXT_PDF',
							'Higher ESG ratings relative to a non-ESG optimized benchmark',
							{},
							{ marginBottom: 10, marginLeft: 3 },
							['text-10']
						)
					],
					{},
					Alignment.Horizontal.Left
				)
			);
			this.modelList = this.selectedModels?.length ? this.selectedModels : this.defineModelList(componentData);
			let proportion = Math.floor(100 / this.modelList.length);
			panels.push(this.buildUpliftChart(componentData, contentSvc));
			if (this.modelList.length <= 6) {
				panels.push(this.buildLegendForModels(this.modelList, proportion));
				panels.push(this.buildLegendForModels(this.modelList, proportion, true));
			} else {
				proportion = Math.floor(100 / 6);
				panels.push(this.buildLegendForModels(this.modelList.slice(0, 6), proportion));
				panels.push(this.buildLegendForModels(this.modelList.slice(0, 6), proportion, true));
				panels.push(this.buildLegendForModels(this.modelList.slice(6), proportion));
				panels.push(this.buildLegendForModels(this.modelList.slice(6), proportion, true));
			}
		}
		return panels;
	}

	public buildAsOfDateRow(componentData: ComponentData): StaticParagraphComponent {
		const tabData = componentData && componentData.success && componentData.getTabById('asOfDate');
		if (TabUtil.isTabValid(tabData)) {
			const asOfDate = new DateFormatter(this.dateFormat).format(tabData.tableData[0]?.cells[0]?.[0]?.formattedValue);
			return new StaticParagraphComponent('ESG_AS_OF_PDF', 'As of {0}', { 0: new DateFormatter(this.dateFormat).format(asOfDate) }, { marginBottom: 15 }, ['text-10']);
		}
	}

	public buildUpliftChart(componentData, contentSvc): RowPanel {
		const chartTabData = componentData && componentData.success && componentData.getTabById('esgScore');
		if (TabUtil.isTabValid(chartTabData)) {
			return new RowPanel(
				'ESG Chart',
				[
					new HighchartsSVGComponent(
						ColumnChartName.EsgUplift,
						{ height: 200 },
						chartTabData,
						ChartType.Column,
						this.highchartsESGOptions(contentSvc.get('ESG_UPLIFT_Y_AXIS_LABEL', 'ESG Score'), this.modelList),
						null
					)
				],
				{ marginBottom: 8, marginTop: 8 },
				Alignment.Horizontal.Center
			);
		}
	}

	private highchartsESGOptions(yAxisText: string, modelList: string[]): Object {
		const config = {
			plotOptions: {
				series: {
					animation: false
				},
				column: {
					colorByPoint: true
				}
			},
			yAxis: {
				title: {
					enabled: true,
					text: yAxisText,
					style: {
						fontFamily: '"FortBook", Arial, sans-serif',
						fontSize: '9px'
					}
				},
				tickInterval: 2,
				endOnTick: false
			},
			hasDataLabels: true,
			esgRatingsScaleMap: this.esgRatingScale,
			categories: modelList,
			useHTML: true
		};
		return config;
	}

	private defineModelList(componentData: ComponentData): string[] {
		const tabData = componentData && componentData.success && componentData.getTabById('esgUpliftModels');
		if (TabUtil.isTabValid(tabData)) {
			const cells = tabData?.tableData?.[0]?.cells ?? [];
			return cells.map(item => {
				return item[0].formattedValue;
			});
		}
	}

	private buildLegendForModels(modelList: string[], proportion: number, benchmark: boolean = false): RowPanel {
		const colors = benchmark ? ColorsConfig.ESG_UPLIFT_BENCHMARK_COLORS : ColorsConfig.ESG_UPLIFT_COLORS;
		const columnList: Panel[] = modelList.map((item, index) => {
			const name = benchmark ? item + ' Benchmark' : item;
			return new ColumnPanel(
				'',
				[
					new RowPanel('', [
						new CellPanel([new SquareRenderer(colors[index], { paddingRight: 2 }, 8, 8)], { proportion: 15 }, Alignment.Horizontal.Left),
						new CellPanel([new StaticParagraphComponent('', name, {}, {}, ['text-9'])], { proportion: 85 }, Alignment.Horizontal.Left)
					])
				],
				{ proportion: proportion },
				Alignment.Horizontal.Left,
				Alignment.Vertical.Top
			);
		});
		if (proportion * modelList.length < 100) {
			const fillProportion = 100 - proportion * modelList.length;
			columnList.push(new ColumnPanel('', [], { proportion: fillProportion }, Alignment.Horizontal.Left, Alignment.Vertical.Top));
		}
		const constraints = benchmark ? { marginBottom: 5, marginLeft: 35 } : { marginLeft: 35 };
		return new RowPanel('Models Legend', columnList, constraints);
	}
}
